let activeLightbox: HTMLDivElement | null = null;
let activeCleanup: (() => void) | null = null;

export function openImageLightbox(src: string, alt = "Uploaded image preview") {
  if (typeof document === "undefined" || !src) return;

  activeCleanup?.();

  const previouslyFocusedElement = document.activeElement as HTMLElement | null;
  const previousOverflow = document.body.style.overflow;
  const backdrop = document.createElement("div");
  const imageContainer = document.createElement("div");
  const image = document.createElement("img");
  const closeButton = document.createElement("button");
  let isClosing = false;

  backdrop.className =
    "fixed inset-0 z-[2147483647] flex items-center justify-center bg-black/80 p-4 backdrop-blur-[2px] sm:p-8";
  backdrop.setAttribute("role", "dialog");
  backdrop.setAttribute("aria-label", "Image preview");
  backdrop.setAttribute("aria-modal", "true");
  backdrop.setAttribute("data-branding-protected", "true");

  imageContainer.className =
    "relative flex max-h-full max-w-full items-center justify-center";

  image.alt = alt;
  image.src = src;
  image.className =
    "max-h-[calc(100vh-4rem)] max-w-[calc(100vw-2rem)] rounded-xl object-contain shadow-[0_24px_80px_rgba(0,0,0,0.55)] sm:max-h-[calc(100vh-6rem)] sm:max-w-[calc(100vw-6rem)]";

  closeButton.type = "button";
  closeButton.textContent = "\u00d7";
  closeButton.setAttribute("aria-label", "Close image preview");
  closeButton.className =
    "absolute right-4 top-4 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-black/45 pb-1 text-[32px] font-light leading-none text-white shadow-lg backdrop-blur-md transition-colors hover:bg-black/70 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/80 sm:right-6 sm:top-6";

  const removeLightbox = () => {
    window.removeEventListener("keydown", handleKeyDown);
    backdrop.remove();
    document.body.style.overflow = previousOverflow;

    if (activeLightbox === backdrop) {
      activeLightbox = null;
      activeCleanup = null;
    }
  };

  const closeLightbox = () => {
    if (isClosing) return;
    isClosing = true;
    backdrop.style.pointerEvents = "none";

    const backdropAnimation = backdrop.animate(
      [{ opacity: 1 }, { opacity: 0 }],
      { duration: 160, easing: "ease-out" },
    );

    backdropAnimation.finished
      .catch(() => undefined)
      .finally(() => {
        removeLightbox();
        previouslyFocusedElement?.focus();
      });
  };

  function handleKeyDown(event: KeyboardEvent) {
    if (event.key === "Escape") closeLightbox();
  }

  backdrop.addEventListener("pointerdown", (event) => {
    if (event.target === backdrop) closeLightbox();
  });
  closeButton.addEventListener("click", closeLightbox);
  window.addEventListener("keydown", handleKeyDown);

  imageContainer.append(image);
  backdrop.append(imageContainer, closeButton);
  document.body.append(backdrop);
  document.body.style.overflow = "hidden";

  activeLightbox = backdrop;
  activeCleanup = removeLightbox;
  closeButton.focus();

  backdrop.animate([{ opacity: 0 }, { opacity: 1 }], {
    duration: 220,
    easing: "ease-out",
  });
  imageContainer.animate(
    [
      { opacity: 0, transform: "translateY(18px) scale(0.82)" },
      { opacity: 1, transform: "translateY(0) scale(1)" },
    ],
    {
      duration: 320,
      easing: "cubic-bezier(0.22, 1, 0.36, 1)",
    },
  );
}
